/**
 * Route Prefetching Utilities
 * Preloads lazy-loaded route components on hover/focus to eliminate cold start delays
 */

// Map of route paths to their lazy component loaders
import { logger } from '@/lib/logger';
const routeLoaders: Record<string, () => Promise<any>> = {
  '/dashboard': () => import('@/app/routes/dashboard/DashboardPage'),
  '/customers': () => import('@/app/routes/customers/CustomersPage'),
  '/vehicles': () => import('@/app/routes/vehicles/VehiclesPage'),
  '/purchase-invoices': () => import('@/app/routes/purchaseInvoices/PurchaseInvoicesPage'),
  '/sales-invoices': () => import('@/app/routes/salesInvoices/SalesInvoicesPage'),
  '/pipeline': () => import('@/app/routes/pipeline/PipelinePage'),
  '/analytics': () => import('@/app/routes/analytics/AnalyticsPage'),
  '/calendar': () => import('@/app/routes/calendar/CalendarPage'),
  '/garage': () => import('@/app/routes/garage'),
  '/settings': () => import('@/app/routes/settings/SettingsPage'),
  '/settings/profile': () => import('@/app/routes/profile/ProfilePage'),
  '/support': () => import('@/app/routes/support/SupportPage'),
  '/profile': () => import('@/app/routes/profile/ProfilePage'),
};

// Track which routes have been prefetched
const prefetchedRoutes = new Set<string>();

/**
 * Prefetch a route component
 * @param path - Route path (e.g., '/customers')
 */
export function prefetchRoute(path: string): void {
  try {
    // Already prefetched
    if (prefetchedRoutes.has(path)) {
      return;
    }

    const loader = routeLoaders[path];
    if (!loader) {
      return;
    }

    // Mark as prefetching to avoid duplicates
    prefetchedRoutes.add(path);

    // Trigger the import (Vite will cache it)
    loader().catch((error) => {
      // In dev mode, downgrade to debug level to avoid console noise
      // Prefetch failures are non-critical - the route will load normally on navigation
      if (import.meta.env.DEV) {
        // Silent in dev - prefetch failures are expected during HMR
      } else {
        logger.warn(`[Prefetch] Failed to prefetch route ${path}:`, error);
      }
      // Remove from set so we can retry later
      prefetchedRoutes.delete(path);
    });
  } catch (error) {
    // Catch any synchronous errors to prevent blocking navigation
    if (!import.meta.env.DEV) {
      logger.error(`[Prefetch] Synchronous error prefetching ${path}:`, error);
    }
  }
}

/**
 * Prefetch multiple routes
 * @param paths - Array of route paths
 */
export function prefetchRoutes(paths: string[]): void {
  paths.forEach(prefetchRoute);
}

/**
 * Prefetch all routes (useful for idle time prefetching)
 */
export function prefetchAllRoutes(): void {
  prefetchRoutes(Object.keys(routeLoaders));
}

/**
 * Hook into navigation link hover/focus events
 * Call this on mouseenter/focus events for navigation links
 */
export function createPrefetchHandler(path: string) {
  return () => prefetchRoute(path);
}
